import * as dateMath from 'date-arithmetic';
import { useEffect, useState } from "react";
import { AmorgRole, CommandResult, rng } from '../../../amorg-common';
import { AmorgUserRole } from '../../../amorg-common/DTOs/AmorgUserRole';
import { createUserRole, deleteUserRole, getRoles, getUserRoles, updateUserRole } from "../../../api/userClientApi";
import { Card } from "../../../designSystem/Card";
import { Label } from "../../../designSystem/Label";
import { Modal } from "../../../designSystem/Modal";
import { Button } from '../../../designSystem/button/Button';
import { ButtonRow } from "../../../designSystem/button/ButtonRow";
import { Flex } from '../../../designSystem/compositeComponents/FlexHelpers';
import { CheckboxField } from "../../../designSystem/formField/CheckboxField";
import { DateInputField } from "../../../designSystem/formField/DateInputField";
import { SelectField } from "../../../designSystem/formField/SelectField";
import { P } from "../../../designSystem/typography/Body";
import { H2 } from "../../../designSystem/typography/Heading";
import React = require("react");
import { ListItem } from '../../../designSystem/List/ListItem';


type UserRolePanelProps = {
    userId: string;
    disabled?:boolean
};
export function UserRolePanel ({ userId, disabled }: UserRolePanelProps): JSX.Element
{

    const [roles, setRoles] = useState<AmorgRole[]>([]);
    const [userRoles, setUserRoles] = useState<AmorgUserRole[]>([]);
    const [inEditUserRole, setInEditUserRole] = useState<AmorgUserRole | null>(null);

    useEffect(() =>
    {
        const getData = async () =>
        {
            setRoles(await getRoles());
        };
        getData();
    }, []);

    useEffect(() =>
    {
        const getData = async () =>
        {
            setUserRoles(await getUserRoles(userId));
        };
        getData();
    }, []);

    async function handleClickAddRole ()
    {
        const id = rng.getRandomId();
        const today = new Date();
        today.setHours(0, 0, 0);
        const result = await createUserRole(id, userId, 'work_environment_representative', today, dateMath.add(today, 1, 'year'), userRoles.find(ur => ur.isCurrentRole) === undefined);
        if (result.isSuccess)
        {
            const userRoles = await getUserRoles(userId);
            setUserRoles(userRoles);
            setInEditUserRole(userRoles.find(ur => ur.id === id) || null);
        }
    }

    function handleClickEditRole (userRoleId: string)
    {
        setInEditUserRole(userRoles.find(ur => ur.id === userRoleId) || null);
    }

    async function handleClickDeleteRole (userRoleId: string)
    {
        await deleteUserRole(userId, userRoleId);
        setUserRoles(await getUserRoles(userId));
        setInEditUserRole(null);
    }

    async function handleUpdate ()
    {
        const userRoles = await getUserRoles(userId);
        setUserRoles(userRoles);
        setInEditUserRole(userRoles.find(ur => ur.id === inEditUserRole?.id) || null);
    }

    const activeRole = userRoles.find(ur => ur.isCurrentRole);
    const inactiveRoles = userRoles.filter(ur => !ur.isCurrentRole);


    return (
        <>

            <Card variant="outlined">
                <H2>AMO Roller</H2>

                <Flex flexDirection="column" gap={32} style={{marginTop:32}}>

                    {!activeRole &&
                        <RoleSection active={false} title="Aktiv rolle">
                            <Flex alignItems="center" style={{ height: '40px' }}>
                                {roles.length === 0
                                    ? <P>Ingen roller oprettet</P>
                                    : <P>Ingen aktiv rolle valgt</P>
                                }
                            </Flex>
                        </RoleSection>
                    }

                    {activeRole &&
                        <RoleSection active={true} title="Aktiv rolle">
                            <RoleListingItem userId={userId} userRole={activeRole} 
                                onClickEdit={disabled ? undefined : handleClickEditRole} 
                                onClickDelete={disabled ? undefined : handleClickDeleteRole} />
                        </RoleSection>
                    }

                    {inactiveRoles.length > 0 &&

                        <RoleSection active={false} title="Inaktive roller">
                            <Flex flexDirection="column" gap={4}>
                                {inactiveRoles.map((r, index) =>
                                    <RoleListingItem key={index} userId={userId} userRole={r} 
                                        onClickEdit={disabled ? undefined : handleClickEditRole} 
                                        onClickDelete={disabled ? undefined : handleClickDeleteRole} />
                                )}
                            </Flex>
                        </RoleSection>
                    }

                    <ButtonRow>
                        <Button
                            variant="outlined"
                            onClick={handleClickAddRole}
                            text={"Tilføj ny rolle"}
                            disabled={disabled} />
                    </ButtonRow>

                </Flex>
            </Card>

            <Modal
                title={"Rediger rolle"}
                content={inEditUserRole &&
                    <EditRolePanel roles={roles} userId={userId} userRole={inEditUserRole}
                        onUpdated={handleUpdate}
                        onDelete={() => handleClickDeleteRole(inEditUserRole.id)}
                        onClose={() => setInEditUserRole(null)} />
                }
                showModal={inEditUserRole !== null}
                onCancel={() => setInEditUserRole(null)} />
        </>
    );
}

type RoleSectionProps = {
    title: string,
    active: boolean,
    children: React.ReactNode;

};
function RoleSection (props: RoleSectionProps): JSX.Element
{

    return (
        <Flex flexDirection="column" gap={4}>
            <Label>{props.title}</Label>
            {props.children}
        </Flex>
    );
}


type RoleListingItemProps = {
    userId: string;
    userRole: AmorgUserRole;
    onClickDelete?: (userRoleId: string) => void;
    onClickEdit?: (userRoleId: string) => void;
};
function RoleListingItem (props: RoleListingItemProps): JSX.Element
{

    const handleClickEdit = () => props.onClickEdit?.(props.userRole.id);

    const formatDate = (date: Date) =>
    {
        let dt = date;
        if (typeof (dt) === 'string')
            dt = new Date(dt);
        return `${dt.getDate()}/${dt.getMonth() + 1}-${dt.getFullYear()}`;
    };

    return (
        <>
            <ListItem
                leadingIcon="interface_user_user"
                primaryText={props.userRole.title}
                secondary={{ text: `${formatDate(props.userRole.start)} - ${formatDate(props.userRole.end)}` }}
                button={{ variant: "outlined", icon: "interface_actions_edit", onClick: handleClickEdit }}
            />

        </>
    );
}

type EditRolePanelProps = {
    roles: AmorgRole[],
    userId: string,
    userRole: AmorgUserRole,
    onUpdated: () => void,
    onDelete: () => void,
    onClose: () => void;
};
function EditRolePanel ({ roles, userId, userRole, onUpdated, onDelete, onClose }: EditRolePanelProps): JSX.Element
{

    function handleResult (result: CommandResult)
    {
        if (result.isSuccess)
            onUpdated();
    }

    async function handleRoleChange (newValue: string)
    {
        handleResult(
            await updateUserRole(userRole.id, userId, newValue, userRole.start, userRole.end, userRole.isCurrentRole)
        );
    }

    async function handleStartChange (value: Date | null)
    {
        if(!value)
            return;

        handleResult(
            await updateUserRole(userRole.id, userId, userRole.roleId, value, userRole.end, userRole.isCurrentRole)
        );
    }

    async function handleEndChange (value: Date | null)
    {
        if(!value)
            return;

        handleResult(
            await updateUserRole(userRole.id, userId, userRole.roleId, userRole.start, value, userRole.isCurrentRole)
        );
    }

    async function handleIsCurrentRoleChange (value: string, checked: boolean)
    {
        handleResult(
            await updateUserRole(userRole.id, userId, userRole.roleId, userRole.start, userRole.end, checked)
        );
    }

    return (
        <Flex flexDirection="column" gap={16}>
            <Flex flexDirection="column" gap={16}>
                <SelectField
                    id="role-selector"
                    label="Rolle"
                    options={roles.map(r => new Option(r.title, r.id))}
                    selectedValue={userRole.roleId}
                    onChange={handleRoleChange} />

                <Flex justifyContent="space-between" gap={5}>
                    <DateInputField
                        id="role-start"
                        label="Fra"
                        width="100%"
                        value={new Date(userRole.start)}
                        onValueChange={handleStartChange}
                        validator={() => { return { valid: true }; }} />

                    <DateInputField
                        id="role-end"
                        label="Til"
                        width="100%"
                        value={new Date(userRole.end)}
                        onValueChange={handleEndChange}
                        validator={() => { return { valid: true }; }} />
                </Flex>

                <CheckboxField
                    id="isCurrent"
                    label="Er den aktuelle rolle"
                    text=""
                    checked={userRole.isCurrentRole}
                    value="isCurrent"
                    onChange={handleIsCurrentRoleChange} />
            </Flex>
            <Flex flexDirection="row" justifyContent="space-between">
                <Button variant="destructive"
                    onClick={onDelete}
                    icon={"interface_actions_delete"}
                    text={"Slet"}
                />

                <Button variant="filled"
                    onClick={onClose}
                    icon={"actions_close"}
                    text={"Luk"}
                />
            </Flex>
        </Flex>
    );
}